
import { useState, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const useBookingAlert = () => {
  const { toast } = useToast();
  const [activeBookings, setActiveBookings] = useState<any[]>([]);

  useEffect(() => {
    const checkActiveBookings = async () => {
      try {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.log("No authenticated user found");
          return;
        }

        // Fetch active bookings
        const { data: bookings, error } = await supabase
          .from('bookings')
          .select(`
            id,
            start_time,
            end_time,
            status,
            overtime_hours,
            overtime_charges,
            actual_end_time,
            parking_spot_id,
            parking_spot:parking_spots (
              price_per_hour
            )
          `)
          .eq('user_id', user.id)
          .eq('status', 'confirmed');

        if (error) {
          console.error('Error fetching active bookings:', error);
          return;
        }

        console.log('Active bookings loaded:', bookings);
        setActiveBookings(bookings || []);
      } catch (err) {
        console.error('Exception in checkActiveBookings:', err);
      }
    };

    // Initial check
    checkActiveBookings();

    // Set up interval to check every minute
    const interval = setInterval(checkActiveBookings, 60000);

    // Subscribe to booking updates
    const channel = supabase
      .channel('booking-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings'
        },
        (payload) => {
          console.log('Booking update received:', payload);
          checkActiveBookings();
        }
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    if (!activeBookings || activeBookings.length === 0) return;
    
    console.log('Checking active bookings for alerts:', activeBookings);
    
    activeBookings.forEach(booking => {
      if (!booking || !booking.end_time) {
        console.warn('Booking missing end_time:', booking);
        return;
      }
      
      // Parse the dates directly from ISO strings
      const endTime = new Date(booking.end_time);
      const now = new Date();
      const timeLeft = endTime.getTime() - now.getTime();
      
      console.log(`Booking ${booking.id} time left: ${timeLeft / (60 * 1000)} minutes`);

      // Alert 30 minutes before end time
      if (timeLeft > 0 && timeLeft <= 30 * 60 * 1000) {
        // Check if we've already shown this notification
        const alertKey = `alert-30min-${booking.id}`;
        if (!localStorage.getItem(alertKey)) {
          toast({
            title: "Booking Ending Soon",
            description: "Your parking booking will end in 30 minutes. Please prepare to vacate the spot.",
          });
          localStorage.setItem(alertKey, 'true');
        }
      }

      // Check for overtime
      if (timeLeft < 0 && booking.parking_spot && booking.parking_spot.price_per_hour) {
        const overtimeHours = Math.ceil(Math.abs(timeLeft) / (60 * 60 * 1000));
        const pricePerHour = parseFloat(booking.parking_spot.price_per_hour);
        
        if (isNaN(pricePerHour)) {
          console.warn('Invalid price_per_hour:', booking.parking_spot.price_per_hour);
          return;
        }
        
        const overtimeCharge = overtimeHours * pricePerHour;

        // Only show overtime notification once
        const overtimeKey = `overtime-${booking.id}`;
        if (!localStorage.getItem(overtimeKey)) {
          toast({
            title: "Parking Time Exceeded",
            description: `You have exceeded your booking time. Additional charges of ₹${overtimeCharge} will apply for ${overtimeHours} extra hour(s).`,
            variant: "destructive",
          });
          localStorage.setItem(overtimeKey, 'true');

          // Update booking with overtime charges
          updateOvertimeCharges(booking.id, overtimeHours, overtimeCharge);
        }
      }
    });
  }, [activeBookings, toast]);

  const updateOvertimeCharges = async (bookingId: string, overtimeHours: number, overtimeCharge: number) => {
    try {
      console.log('Updating overtime charges:', { bookingId, overtimeHours, overtimeCharge });
      const { error } = await supabase
        .from('bookings')
        .update({
          overtime_hours: overtimeHours,
          overtime_charges: overtimeCharge,
          updated_at: new Date().toISOString()
        })
        .eq('id', bookingId);

      if (error) {
        console.error('Error updating overtime charges:', error);
        throw error;
      }
      console.log('Overtime charges updated successfully');
    } catch (error) {
      console.error('Error updating overtime charges:', error);
    }
  };
};
